// OGC-531 Creatives Manager v19 catalog: search, status/type filters, sort,
// real server pagination, preview, Edit/View and the anchored Actions menu
// (Edit/View, Archive or Unarchive, Delete) with its confirmation dialogs.
import { createElement, useEffect, useRef, useState } from 'react';
import Menu from '../../components/Menu.jsx';
import Select from '../../components/Select.jsx';
import { CatalogThumbnail, ManagementPagination, ManagementStatus, useManagementData } from '../PlatformManagement.jsx';
import { CmIcon } from './creatives-icons.jsx';
import useCreativeLifecycle from './useCreativeLifecycle.jsx';

const STATUS_OPTIONS = Object.freeze([
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
]);
const SORT_OPTIONS = Object.freeze([
  { value: 'updated', label: 'Recently updated' },
  { value: 'name', label: 'Name A–Z' },
]);

function formatDate(value) {
  return value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
}

export function creativeRowSubtitle(row) {
  if (row.status === 'archived') return 'Archived history';
  if (row.pending_version_number) return 'Draft changes available';
  if (row.status === 'published') return 'Available for new campaigns';
  return 'Not published';
}

// Ready offerings: a type with at least one Published creative (OGC-531).
export function catalogCoverage(types = []) {
  const active = types.filter((type) => !type.archivedAt);
  return { active, ready: active.filter((type) => Number(type.publishedChoices) >= 1).length };
}

function directoryQueryString(query) {
  return new URLSearchParams(
    Object.entries({
      search: query.search,
      status: query.status,
      type: query.type,
      sort: query.sort,
      limit: query.limit,
      offset: query.offset,
    }).filter(([, value]) => value !== '' && value != null),
  ).toString();
}

export default function CreativesCatalog({
  apiClient,
  query,
  onQueryChange,
  revision,
  onRefresh,
  onToast,
  onOpenCreative,
  onAddCreative,
  onManageTypes,
  onUnarchived,
  focusHeadingOnMount = false,
}) {
  const state = useManagementData(`/api/saas/platform/catalog/directory?${directoryQueryString(query)}`, revision, { keepData: true });
  const types = state.data?.types || [];
  const rows = state.data?.rows || [];
  const [search, setSearch] = useState(query.search || '');
  const [notice, setNotice] = useState(null);
  const headingRef = useRef(null);
  const searchTimer = useRef(null);
  const lifecycle = useCreativeLifecycle({
    apiClient,
    types,
    onChanged: onRefresh,
    onToast,
    onManageTypes,
    onUnarchived,
    onNotice: setNotice,
    onDeleted: (row) => { focusAfterDelete.current = row.id; },
  });

  // After a delete, move focus to the heading once the row has left the
  // refreshed list (and its dialog has closed), not on a timer that can run
  // while the modal dialog still makes the page inert.
  const focusAfterDelete = useRef(null);
  useEffect(() => {
    const id = focusAfterDelete.current;
    if (!id || rows.some((row) => row.id === id) || lifecycle.open) return;
    focusAfterDelete.current = null;
    headingRef.current?.focus({ preventScroll: true });
  }, [rows, lifecycle.open]);

  // Follow the URL, but keep what is typed (trailing spaces included) when it
  // is the search already applied.
  useEffect(() => {
    setSearch((current) => {
      if (current.trim() === (query.search || '')) return current;
      clearTimeout(searchTimer.current);
      return query.search || '';
    });
  }, [query.search]);
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  useEffect(() => {
    if (focusHeadingOnMount) headingRef.current?.focus({ preventScroll: true });
  }, [focusHeadingOnMount]);
  useEffect(() => {
    const total = state.data?.total;
    const offset = Number(query.offset);
    const limit = Number(query.limit);
    if (total != null && offset > 0 && offset >= total) {
      onQueryChange({ offset: String(Math.max(0, Math.ceil(total / limit) - 1) * limit) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data?.total, query.offset, query.limit]);

  function changeSearch(value) {
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onQueryChange({ search: value.trim() }), 300);
  }

  function clearFilters() {
    clearTimeout(searchTimer.current);
    setSearch('');
    onQueryChange({ search: '', status: '', type: '' });
  }

  async function retryCleanup() {
    if (notice?.kind !== 'cleanup') return;
    try {
      const cleanup = await apiClient.cleanUpPackageStorage(notice.packageId, `catalog-storage-cleanup-${notice.packageId}-${Date.now()}`);
      if (Number(cleanup?.objectCleanupPending) > 0) {
        setNotice({ ...notice, retryFailed: true });
      } else {
        setNotice(null);
        onToast('Stored files cleaned up.');
      }
    } catch {
      setNotice({ ...notice, retryFailed: true });
    }
  }

  const coverage = catalogCoverage(types);
  const filtered = Boolean(query.search || query.status || query.type);
  const total = state.data?.total;

  return (
    <>
      <header className="management-page-heading">
        <div>
          <span className="management-eyebrow">Platform administration</span>
          <h1 ref={headingRef} tabIndex={-1}>Creative catalog</h1>
          <p>Keep your ad library current, from draft to published.</p>
        </div>
        <div className="header-actions">
          <button type="button" onClick={onManageTypes}>Renovation types</button>
          <button type="button" className="management-primary" onClick={() => onAddCreative(types.filter((type) => !type.archivedAt))}>
            Add creative <span aria-hidden="true">&nbsp; +</span>
          </button>
        </div>
      </header>

      {notice?.kind === 'cleanup' ? (
        <div className="notice warn page-notice" role="status">
          <span>{notice.retryFailed
            ? 'Some stored files for the deleted draft still need cleanup.'
            : 'The draft was deleted. Some of its stored files still need cleanup.'}</span>
          <button type="button" onClick={retryCleanup}>Retry cleanup</button>
        </div>
      ) : null}

      <div className="management-toolbar">
        <label className="management-search">
          <span className="sr-only">Search creatives</span>
          <input type="search" placeholder="Search creatives…" value={search} maxLength={160} onChange={(event) => changeSearch(event.target.value)} />
        </label>
        <Select aria-label="Filter by status" value={query.status || ''} onChange={(status) => onQueryChange({ status })} options={STATUS_OPTIONS} />
        <Select
          aria-label="Filter by renovation type"
          value={query.type || ''}
          onChange={(type) => onQueryChange({ type })}
          options={[
            { value: '', label: 'All renovation types' },
            ...types.map((type) => ({ value: type.id, label: `${type.displayName}${type.archivedAt ? ' (archived)' : ''}` })),
          ]}
        />
        <Select aria-label="Sort creatives" value={query.sort || 'updated'} onChange={(sort) => onQueryChange({ sort })} options={SORT_OPTIONS} />
        <button type="button" className="refresh-action" aria-label="Refresh creatives" title="Refresh creatives" onClick={onRefresh}>
          <CmIcon name="refresh" />
        </button>
      </div>

      <div className="management-panel">
        <div className="management-table-wrap">
          <table className="management-table">
            <thead>
              <tr>
                <th>Creative</th>
                <th>Status</th>
                <th>Renovation type</th>
                <th>Version</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const archived = row.status === 'archived';
                return (
                  <tr key={row.id}>
                    <td data-label="Creative">
                      <div className="management-name">
                        <CatalogThumbnail row={row} />
                        <div>
                          <button type="button" className="name-button" onClick={() => onOpenCreative(row)}>{row.name}</button>
                          <small>{creativeRowSubtitle(row)}</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="Status"><ManagementStatus value={row.status} /></td>
                    <td data-label="Renovation type">{row.renovation_type}</td>
                    <td data-label="Version">{row.version_number ? `v${row.version_number}` : 'No version'}</td>
                    <td data-label="Updated">{formatDate(row.updated_at)}</td>
                    <td data-label="Actions">
                      <div className="row-actions">
                        {row.version_id ? (
                          <button type="button" aria-label={`Preview ${row.name}`} title="Preview" onClick={(event) => lifecycle.preview(row, event.currentTarget)}>
                            <CmIcon name="play" />
                          </button>
                        ) : null}
                        <button type="button" className="edit-action" onClick={() => onOpenCreative(row)}>{archived ? 'View' : 'Edit'}</button>
                        <Menu
                          label={`More actions for ${row.name}`}
                          items={[
                            { id: 'open', label: archived ? 'View creative' : 'Edit creative', icon: <CmIcon name="edit" size={18} />, onSelect: () => onOpenCreative(row) },
                            archived
                              ? { id: 'unarchive', label: 'Unarchive creative', icon: <CmIcon name="unarchive" size={18} />, onSelect: () => lifecycle.unarchive(row) }
                              : { id: 'archive', label: 'Archive creative', icon: <CmIcon name="archive" size={18} />, onSelect: () => lifecycle.archive(row) },
                            { id: 'delete', label: 'Delete creative', icon: <CmIcon name="trash" size={18} />, tone: 'danger', separatorBefore: true, onSelect: () => lifecycle.requestDelete(row) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {state.status === 'ready' && !rows.length ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">
                      <h2>No creatives found</h2>
                      <p>{filtered ? 'Try another name or clear your filters.' : 'Add your first creative to start the catalog.'}</p>
                      {filtered ? <button type="button" onClick={clearFilters}>Clear filters</button> : null}
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {state.status === 'loading' && !state.data ? <div className="management-empty" role="status">Loading creatives…</div> : null}
        {state.error ? (
          <div className="management-error" role="alert">
            The creative catalog could not be loaded. <button type="button" onClick={onRefresh}>Retry</button>
          </div>
        ) : null}
        {total != null && total <= Number(query.limit || 25) && Number(query.offset || 0) === 0 ? (
          <footer className="creatives-results">
            <span aria-live="polite">{`${total} creative${total === 1 ? '' : 's'}`}</span>
            <span>All results</span>
          </footer>
        ) : (
          <ManagementPagination
            data={state.data}
            query={query}
            onChange={onQueryChange}
            summary={total == null ? null : `${total} creative${total === 1 ? '' : 's'} · ${Number(query.offset || 0) + 1}–${Math.min(Number(query.offset || 0) + Number(query.limit || 25), total)}`}
          />
        )}
      </div>

      {state.data ? createElement(
        'details',
        { className: 'management-coverage', role: 'region', 'aria-label': 'Catalog coverage' },
        createElement('summary', null, 'Catalog coverage ', createElement('span', null, `${coverage.ready} of ${coverage.active.length} types ready`)),
        <ul>
          {coverage.active.map((type) => (
            <li key={type.id}>
              <strong>{type.displayName}</strong>
              <span>
                {type.publishedChoices} published choices
                {Number(type.publishedChoices) < 1 ? ' · Needs a Published creative' : ''}
              </span>
            </li>
          ))}
        </ul>,
        <button type="button" onClick={onManageTypes}>Manage renovation types</button>,
      ) : null}

      {lifecycle.dialog}
    </>
  );
}
